import { useEffect, useState } from 'react'
import type { FC } from 'react'
import { Modal, Paper } from '@mui/material'
import { Draggable, type DraggableBounds } from '~/shared/ui'
import debounce from 'lodash/debounce'

import { useActiveVideoVisit } from '~/shared/hooks/activeVideoVisit'
import { AppointmentEntry } from './AppointmentEntry'

const MULTIPLIER = 0.8
const getHeight = () => Math.round(window.innerHeight * MULTIPLIER)
const getWidth = () => Math.round(getHeight() * MULTIPLIER)
const getBounds = (modalHeight: number, modalWidth: number): DraggableBounds => ({
  left: -((window.innerWidth - modalWidth) / 2),
  top: -((window.innerHeight - modalHeight) / 2),
  right: (window.innerWidth - modalWidth) / 2,
  bottom: (window.innerHeight - modalHeight) / 2,
})

const DRAGGABLE_HANDLE_ID = 'VIDEO_VISIT_MODAL'

const AppointmentModal: FC = () => {
  const { activeVideoVisitId } = useActiveVideoVisit()
  const [height, setHeight] = useState<number>(getHeight())
  const [width, setWidth] = useState<number>(getWidth())

  // Change modal bounds when window size changes. The debounced handler is created inside
  // the effect so the listener that is added is the one that gets removed — it used to be
  // rebuilt on every render, and the effect never removed it at all.
  useEffect(() => {
    const handleResize = debounce(
      () => {
        setHeight(getHeight())
        setWidth(getWidth())
      },
      250,
      { trailing: true },
    )
    window.addEventListener('resize', handleResize)

    return () => {
      handleResize.cancel()
      window.removeEventListener('resize', handleResize)
    }
  }, [])
  const bounds = getBounds(height, width)

  return activeVideoVisitId ? (
    <Modal disableAutoFocus disableEnforceFocus hideBackdrop open sx={{ pointerEvents: 'none' }}>
      <div>
        <Draggable
          bounds={bounds}
          handle={`#${DRAGGABLE_HANDLE_ID}`}
          positionOffset={{
            x: '-50%',
            y: '-50%',
          }}
        >
          <Paper
            elevation={10}
            sx={{
              height,
              width,
              pointerEvents: 'all',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              position: 'absolute',
              overflow: 'hidden',
            }}
          >
            <AppointmentEntry modal />
          </Paper>
        </Draggable>
      </div>
    </Modal>
  ) : null
}

export default AppointmentModal
